/* eslint-disable @typescript-eslint/no-explicit-any */
import { Fragment, useEffect, useRef, useState } from "react";
import classnames from "classnames";
import PerfectScrollbar from "react-perfect-scrollbar";
import { Bell } from "react-feather";
import {
  Button,
  Badge,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { io, Socket } from "socket.io-client";
import { useNavigate } from "react-router-dom";
import Avatar from "@components/avatar";
import { environmentConfig } from "@src/configs/environmentConfig";
import { authService } from "@src/@api-services/auth";

type AppNotification = {
  id: number;
  subject?: string | null;
  body?: string | null;
  title?: string | null;
  url?: string | null;
};

const stripHtml = (value?: string | null) => {
  if (!value) return "";
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const isExternalUrl = (url: string) => /^https?:\/\//i.test(url);

const NotificationDropdown = () => {
  const navigate = useNavigate();
  const socketRef = useRef<Socket | null>(null);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);

  useEffect(() => {
    const token = authService.getToken();
    const socketBaseUrl = environmentConfig.BACKEND_URL;
    if (!token || !socketBaseUrl) {
      console.warn("[Socket] skipped: missing token or backend URL");
      return;
    }

    const socket = io(socketBaseUrl, {
      path: "/api/v1/socket",
      auth: { token },
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("[Socket] connected", socket.id);
    });

    socket.on("notifications", (data: AppNotification[]) => {
      console.log("[Socket] notifications loaded", data);
      setNotifications(Array.isArray(data) ? data : []);
    });

    socket.on("new-notification", (notif: AppNotification) => {
      console.log("[Socket] new-notification", notif);
      setNotifications((prev) => [notif, ...prev]);
    });

    socket.on("read-success", ({ id }: { id: number }) => {
      setNotifications((prev) => prev.filter((item) => item.id !== id));
    });

    socket.on("auth-error", (err: { message?: string }) => {
      console.error("Socket auth error:", err?.message);
    });

    socket.on("connect_error", (err) => {
      console.error("Socket connection error:", err.message);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  const markAsRead = (id: number) => {
    socketRef.current?.emit("read", { notificationId: id });
  };

  const markAllAsRead = () => {
    notifications.forEach((item) => markAsRead(item.id));
  };

  const openNotificationUrl = (url?: string | null) => {
    const target = url?.trim();
    if (!target) return;

    if (isExternalUrl(target)) {
      window.open(target, "_blank", "noopener,noreferrer");
      return;
    }

    navigate(target.startsWith("/") ? target : `/${target}`);
  };

  const handleNotificationClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    item: AppNotification
  ) => {
    event.preventDefault();
    markAsRead(item.id);
    openNotificationUrl(item.url);
  };

  const renderNotificationItems = () => {
    if (!notifications.length) {
      return (
        <li className="dropdown-menu-header">
          <div className="text-center text-muted py-2 px-1">
            No new notifications
          </div>
        </li>
      );
    }

    return (
      <PerfectScrollbar
        component="li"
        className="media-list scrollable-container"
        options={{ wheelPropagation: false }}
      >
        {notifications.map((item) => {
          const subject =
            item.subject?.trim() || item.title?.trim() || "Notification";
          const bodyText = stripHtml(item.body);
          const href = item.url?.trim() || "#";

          return (
            <a
              key={item.id}
              className="d-flex"
              href={href}
              onClick={(event) => handleNotificationClick(event, item)}
            >
              <div
                className={classnames("list-item d-flex", {
                  "align-items-start": true,
                })}
              >
                <Fragment>
                  <div className="me-1">
                    <Avatar
                      color="light-primary"
                      icon={<Bell size={14} />}
                    />
                  </div>
                  <div className="list-item-body flex-grow-1">
                    <p className="media-heading mb-0">
                      <span className="fw-bolder">{subject}</span>
                    </p>
                    {bodyText ? (
                      <small className="notification-text">{bodyText}</small>
                    ) : null}
                  </div>
                </Fragment>
              </div>
            </a>
          );
        })}
      </PerfectScrollbar>
    );
  };

  const unreadCount = notifications.length;

  return (
    <UncontrolledDropdown tag="li" className="dropdown-notification nav-item me-25">
      <DropdownToggle
        tag="a"
        className="nav-link"
        href="/"
        onClick={(e) => e.preventDefault()}
      >
        <Bell size={21} />
        {unreadCount > 0 ? (
          <Badge pill color="danger" className="badge-up">
            {unreadCount > 99 ? "99+" : unreadCount}
          </Badge>
        ) : null}
      </DropdownToggle>
      <DropdownMenu end tag="ul" className="dropdown-menu-media mt-0">
        <li className="dropdown-menu-header">
          <DropdownItem className="d-flex" tag="div" header>
            <h4 className="notification-title mb-0 me-auto">Notifications</h4>
            {unreadCount > 0 ? (
              <Badge tag="div" color="light-primary" pill>
                {unreadCount} New
              </Badge>
            ) : null}
          </DropdownItem>
        </li>
        {renderNotificationItems()}
        <li className="dropdown-menu-footer">
          <Button
            color="primary"
            block
            disabled={!unreadCount}
            onClick={markAllAsRead}
          >
            Read all notifications
          </Button>
        </li>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

export default NotificationDropdown;
